import { SuiteApiClient } from "@weco/suite-client";
import type { AppNotification, NotificationsPage, UnreadCount } from "./types";

/** vite-suite-proxy (dev) and nginx (prod) map this onto the service's `/api/v1.0/`. */
export const NOTIFICATIONS_API_BASE = "/api/notifications";

export interface NotificationsApi {
  list(opts?: { cursor?: string | null; signal?: AbortSignal }): Promise<NotificationsPage>;
  unreadCount(signal?: AbortSignal): Promise<UnreadCount>;
  markRead(id: string): Promise<void>;
  markAllRead(opts?: { category?: string; group_key?: string }): Promise<void>;
}

let _client: SuiteApiClient | null = null;

/** Lazily constructed so importing the suite-chrome barrel never builds a client. */
function client(): SuiteApiClient {
  return (_client ??= new SuiteApiClient(NOTIFICATIONS_API_BASE));
}

/** The `cursor` query param of a DRF `next` URL (absolute or relative). */
export function cursorFromNext(next: string | null | undefined): string | null {
  if (!next) return null;
  try {
    return new URL(next, "http://x").searchParams.get("cursor");
  } catch {
    return null;
  }
}

function normalisePage(raw: unknown): NotificationsPage {
  // Tolerate an unpaginated array, in case the endpoint is served without
  // its pagination class during a deploy.
  if (Array.isArray(raw)) return { results: raw as AppNotification[], next: null };
  const page = (raw ?? {}) as Partial<NotificationsPage>;
  return { results: page.results ?? [], next: page.next ?? null };
}

export const notificationsApi: NotificationsApi = {
  async list({ cursor, signal } = {}) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return normalisePage(await client().get(`/notifications/${qs}`, { signal }));
  },
  async unreadCount(signal) {
    const data = await client().get<Partial<UnreadCount>>("/notifications/unread-count/", { signal });
    return { total: Number(data.total ?? 0), by_category: data.by_category ?? {} };
  },
  async markRead(id) {
    await client().post(`/notifications/${encodeURIComponent(id)}/read/`);
  },
  async markAllRead(opts = {}) {
    await client().post("/notifications/read-all/", opts);
  },
};
